// The workflows table: name, task type, status, the latest published version, a draft mark, its
// rating and usage (sortable; unrated sorts last) and when it last changed, with a menu for
// status changes and delete.
import { DotsThreeVertical, PencilSimpleLine } from "@phosphor-icons/react";
import { Link } from "react-router";
import { ActionIcon, Dropdown } from "rizzui";

import { CatalogIcon } from "../../catalog/icons";
import { STATUS_TONE, statusActions } from "../../catalog/status";
import type { TaskType, TemplateStatus, WorkflowSummary } from "../../catalog/types";
import { type Messages, useT } from "../../i18n";
import { ago } from "../../pages/format";
import { RatingSummary } from "../../ratings/Stars";
import { type Column, DataTable } from "../../ui/DataTable";
import { Mono, Pill, StatusPill } from "../../ui/status";

export interface RowActions {
  setStatus: (w: WorkflowSummary, status: TemplateStatus) => void;
  remove: (w: WorkflowSummary) => void;
}

function RowMenu({ workflow, actions }: { workflow: WorkflowSummary; actions: RowActions }) {
  const { workflows, common } = useT();
  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger aria-label={workflows.list.table.actionsFor(workflow.name)}>
        <ActionIcon as="span" size="sm" variant="outline">
          <DotsThreeVertical weight="bold" className="h-4 w-4" />
        </ActionIcon>
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-52 dark:bg-gray-100">
        {statusActions(workflow).map((a) => (
          <Dropdown.Item
            key={a.status}
            className="flex-col items-start rounded-md"
            disabled={a.blocked !== undefined}
            onClick={() => actions.setStatus(workflow, a.status)}
          >
            <span>{a.label}</span>
            {a.blocked ? <span className="text-xs text-gray-500">{a.blocked}</span> : null}
          </Dropdown.Item>
        ))}
        <Dropdown.Item className="rounded-md text-red" onClick={() => actions.remove(workflow)}>
          {common.actions.delete}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

type Copy = Messages["workflows"]["list"]["table"] & { ratings: Messages["ratings"] };

function columns(t: Copy, taskTypes: Map<string, TaskType>, actions: RowActions): Column<WorkflowSummary>[] {
  return [
    {
      key: "name",
      header: t.workflow,
      cell: (w) => (
        <div className="flex items-center gap-3">
          <CatalogIcon name={w.icon} className="hidden sm:flex" />
          <div className="min-w-0">
            <Link to={`/workflows/${w.id}`} className="block truncate font-semibold text-gray-900 hover:text-primary">
              {w.name}
            </Link>
            <Mono className="text-xs text-gray-500">{w.slug}</Mono>
          </div>
        </div>
      ),
    },
    { key: "type", header: t.taskType, hideBelow: "md", cell: (w) => taskTypes.get(w.task_type_id)?.name ?? "—" },
    { key: "status", header: t.status, cell: (w) => <StatusPill tone={STATUS_TONE[w.status]} status={w.status} /> },
    {
      key: "version",
      header: t.published,
      hideBelow: "sm",
      cell: (w) => (
        <span className="flex items-center gap-2">
          {w.latest_version === null ? <span className="text-gray-500">—</span> : <Mono>v{w.latest_version}</Mono>}
          {w.draft_version !== null ? (
            <Pill tone="orange" icon={<PencilSimpleLine weight="bold" />}>
              {t.draftVersion(w.draft_version)}
            </Pill>
          ) : null}
        </span>
      ),
    },
    {
      key: "rating",
      header: t.ratings.columns.rating,
      hideBelow: "md",
      sortValue: (w) => w.stats.rating ?? -1,
      cell: (w) => <RatingSummary stats={w.stats} />,
    },
    {
      key: "usage",
      header: t.ratings.columns.usage,
      hideBelow: "xl",
      sortValue: (w) => w.stats.usage_count,
      cell: (w) => <span className="tabular whitespace-nowrap">{t.ratings.usedIn(w.stats.usage_count)}</span>,
    },
    { key: "updated", header: t.updated, hideBelow: "lg", cell: (w) => <span className="whitespace-nowrap">{ago(w.updated_at)}</span> },
    { key: "actions", header: "", className: "text-end", cell: (w) => <RowMenu workflow={w} actions={actions} /> },
  ];
}

export function WorkflowsTable({ rows, taskTypes, actions }: { rows: WorkflowSummary[]; taskTypes: TaskType[]; actions: RowActions }) {
  const { workflows, ratings } = useT();
  const t = workflows.list.table;
  const byId = new Map(taskTypes.map((tt) => [tt.id, tt]));
  return <DataTable label={t.label} columns={columns({ ...t, ratings }, byId, actions)} rows={rows} rowKey={(w) => w.id} />;
}
